import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import useMapbox from '@/composables/useMapbox'
import { ADD_MAP_LAYER, REMOVE_MAP_LAYER } from '@/store/mutation-types'
import {
  CustomLayerInterface,
  GeoJSONSource,
  GeoJSONSourceSpecification,
  MapMouseEvent,
  LayerSpecification
} from 'mapbox-gl'
import { onBeforeUnmount, onMounted, toRefs, watch } from 'vue'
import { useStore } from 'vuex'

export type AnyLayerNotCustom = Exclude<LayerSpecification, CustomLayerInterface>
export type GeojsonLayerEvent = MapMouseEvent & {
  features?: GeoJSON.Feature<GeoJSON.Geometry>[]
}

type Props = {
  before?: string
  layer: AnyLayerNotCustom
  source: GeoJSONSourceSpecification | null
  sourceId: string
}

type Emits = {
  (event: 'layerClicked', mouseEvent: mapboxgl.MapMouseEvent): void
  (event: 'layerMouseEnter', mouseEvent: mapboxgl.MapMouseEvent): void
  (event: 'layerMouseLeave', mouseEvent: mapboxgl.MapMouseEvent): void
  (event: 'layerMouseMove', mouseEvent: mapboxgl.MapMouseEvent): void
  (event: 'layerRightClicked', mouseEvent: mapboxgl.MapMouseEvent): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GeojsonLayer',
  props: {
    before: {},
    layer: {},
    source: {},
    sourceId: {}
  },
  emits: ["layerClicked", "layerMouseEnter", "layerMouseLeave", "layerMouseMove", "layerRightClicked"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emits = __emit

const { map } = useMapbox()
const store = useStore()

// MANUAL RERENDER

const { layer, source } = toRefs(props)

watch(
  layer,
  () => {
    updateLayerPaint()
    updateLayerLayout()
    updateLayerFilter()
  },
  { deep: true }
)

watch(
  source,
  () => {
    if (props.source?.data) {
      if (!map) return
      addLayerAndSource()
      const source = map.getSource(props.sourceId) as GeoJSONSource
      if (typeof props.source.data === 'string') {
        source.setData(props.source.data)
      } else if (props.source.data.type == 'FeatureCollection') {
        source.setData(props.source.data)
      } else if (props.source.data.type == 'Feature') {
        source.setData(props.source.data)
      } else {
        source.setData({
          type: 'Feature',
          geometry: props.source.data,
          properties: {}
        })
      }
    }
  },
  { deep: true }
)

// LIFECYCLE

onMounted(() => {
  addLayerAndSource()
  addEventListeners()
})

onBeforeUnmount(() => {
  removeEventListeners()
  removeLayerAndSource()
})

// LAYER AND SOURCE

function addLayerAndSource() {
  if (!map) return
  if (!props.source) return
  if (!map.getSource(props.sourceId)) map.addSource(props.sourceId, props.source)
  if (!map.getLayer(props.layer.id)) {
    const before = props.before && map.getLayer(props.before) ? props.before : undefined
    map.addLayer({ ...props.layer, source: props.sourceId }, before)
    store.commit(ADD_MAP_LAYER, props.layer.id)
  }
}

function removeLayerAndSource() {
  if (!map) return
  if (map.getLayer(props.layer.id)) {
    map.removeLayer(props.layer.id)
    store.commit(REMOVE_MAP_LAYER, props.layer.id)
  }
  if (map.getSource(props.sourceId)) {
    // Multiple layers may use the same source
    const layersWithSameSource = map
      ?.getStyle()
      ?.layers?.filter((el) => (el.type as string) !== 'custom' && el.source === props.sourceId)
    if (!layersWithSameSource || layersWithSameSource.length === 0) map.removeSource(props.sourceId)
  }
}

function updateLayerPaint() {
  if (!map) return
  if (!props.layer.paint) return
  for (const property in props.layer.paint) {
    map.setPaintProperty(props.layer.id, property, props.layer.paint[property])
  }
}
function updateLayerLayout() {
  if (!map) return
  if (!props.layer.layout) return
  for (const property in props.layer.layout) {
    map.setLayoutProperty(props.layer.id, property, props.layer.layout[property])
  }
}
function updateLayerFilter() {
  if (!map) return
  if (!props.layer.filter) return
  map.setFilter(props.layer.id, props.layer.filter)
}

// EVENT LISTENERS

const onClick = (e: mapboxgl.MapLayerMouseEvent) => emits('layerClicked', e)
const onMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => emits('layerMouseEnter', e)
const onMouseLeave = (e: mapboxgl.MapLayerMouseEvent) => emits('layerMouseLeave', e)
const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => emits('layerMouseMove', e)
const onRightClick = (e: mapboxgl.MapLayerMouseEvent) => emits('layerRightClicked', e)

function addEventListeners() {
  if (map) {
    map.on('click', props.layer.id, onClick)
    map.on('mouseenter', props.layer.id, onMouseEnter)
    map.on('mouseleave', props.layer.id, onMouseLeave)
    map.on('mousemove', props.layer.id, onMouseMove)
    map.on('contextmenu', props.layer.id, onRightClick)
  }
}

function removeEventListeners() {
  if (map) {
    map.off('click', props.layer.id, onClick)
    map.off('mouseenter', props.layer.id, onMouseEnter)
    map.off('mouseleave', props.layer.id, onMouseLeave)
    map.off('mousemove', props.layer.id, onMouseMove)
    map.off('contextmenu', props.layer.id, onRightClick)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div"))
}
}

})